import { useMutation, useQueryClient } from 'react-query'
import { useRouter } from 'next/router'
import { useLessonKey, useLesson } from 'querys/lesson'
import {
  lessonCompletedService,
  videoProgressService,
  lessonFavoriteService,
} from 'services/lesson'
import useSession from 'querys/session'

export function useRemoveFavorite() {
  const { token } = useSession()
  return useMutation(
    (lesson_id) => lessonFavoriteService({ lesson_id, token, favorite: false }),
  )
}

export function useLessonFavorite() {
  const { token } = useSession()
  const queryClient = useQueryClient()
  const LESSON_KEY = useLessonKey()
  const { data: { uuid } } = useLesson()
  return useMutation(
    (favorite) => lessonFavoriteService({ lesson_id: uuid, token, favorite }),
    {
      onMutate: async (favorite) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(LESSON_KEY)

        // Snapshot the previous value
        const previousTodos = queryClient.getQueryData('LESSON_KEY')

        // Optimistically update to the new value
        queryClient.setQueryData(LESSON_KEY, (old) => ({ ...old, favorite }))

        // Return a context object with the snapshotted value
        return { previousTodos }
      },
      // If the mutation fails, use the context returned from onMutate to roll back
      onError: (err, favoriteLesson, context) => {
        queryClient.setQueryData(LESSON_KEY, context.previousTodos)
      },
      // Always refetch after error or success:
      onSettled: () => {
        queryClient.invalidateQueries(LESSON_KEY)
      },
    },
  )
}

export function useLessonCompleted() {
  const router = useRouter()
  const { course } = router.query
  const { token } = useSession()
  const queryClient = useQueryClient()
  const LESSON_KEY = useLessonKey()
  return useMutation(
    (lessonSlug) => lessonCompletedService({ course, lessonSlug, token }),
    {
      onSuccess: (data, lessonSlug) => {
        queryClient.setQueryData(LESSON_KEY, (lessonData) => ({ ...lessonData, completed: true }))
        queryClient.setQueryData(
          ['GET_LESSONS', course, token],
          (chapters) => chapters.map((chapter) => {
            const newLessons = chapter.lessons.map(
              (lesson) => {
                if (lesson.slug === lessonSlug) {
                  return { ...lesson, completed: true }
                }
                return lesson
              },
            )
            return { ...chapter, lessons: newLessons }
          }),
        )
      },
    },
  )
}

export function useVideoProgress() {
  const LESSON_KEY = useLessonKey()
  const [key, course, lesson_slug, token] = LESSON_KEY
  return useMutation(
    (progress) => videoProgressService({
      course, lesson_slug, token, progress,
    }),
  )
}
